import { useState } from 'react';

import { FieldLabel } from '../../shared/ui/field-label';
import { Hint } from '../../shared/ui/hint';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Select } from '../../shared/ui/select';
import { ApiError } from '../../shared/api/client';
import { roleOptionsFor, type IActor, type TRole } from '../../entities/organization';
import type { TMembers } from './use-members';

interface IInviteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  actor: IActor | null;
  members: TMembers;
}

export const InviteDialog = ({ open, onOpenChange, actor, members }: IInviteDialogProps) => {
  const [email, setEmail] = useState('');
  const [as, setAs] = useState<TRole>('member');
  const options = roleOptionsFor(actor);
  const error = members.invite.error;

  const close = (next: boolean) => {
    if (!next) {
      setEmail('');
      setAs('member');
      members.invite.reset();
    }
    onOpenChange(next);
  };

  const send = () =>
    members.invite.mutate({ email: email.trim(), as }, { onSuccess: () => close(false) });

  return (
    <Modal
      open={open}
      onOpenChange={close}
      title="Invite people"
      description="We email them a link to join. It works for a few days, and you can cancel it until it’s used."
      submit={{
        label: 'Send invitation',
        onSubmit: send,
        disabled: !email.includes('@'),
        waiting: members.invite.isPending,
        waitingLabel: 'Sending',
      }}
    >
      <div className="flex flex-col gap-4 pt-2">
        <div>
          <FieldLabel htmlFor="invite-email">Email</FieldLabel>
          <Input
            id="invite-email"
            mono
            autoFocus
            value={email}
            placeholder="name@company.com"
            onChange={(event) => setEmail(event.target.value)}
          />
        </div>
        <div>
          <FieldLabel htmlFor="invite-role">Role</FieldLabel>
          <Select id="invite-role" value={as} onChange={setAs} options={options} />
          {actor?.role !== 'owner' && <Hint>Only an owner can invite another owner.</Hint>}
        </div>

        {error && (
          <Note tone="danger">
            {error instanceof ApiError
              ? error.message
              : 'The invitation was not sent. Nothing was created.'}
          </Note>
        )}
      </div>
    </Modal>
  );
};
